import { useLazyQuery } from "@apollo/client";
import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { FormattedMessage, useIntl } from "react-intl";
import { StringParam, useQueryParams, withDefault } from "use-query-params";

import CircleButton from "@components/buttons/CircleButton";
import ClientAvatar from "@components/ClientAvatar";
import DateStamp from "@components/dates/DateStamp";
import ExpertAvatar from "@components/ExpertAvatar";
import SearchInput from "@components/fieldsNative/SearchInput";
import { ChatsLoader } from "@components/loaders/ChatsLoader";
import { InboxContext } from "@components/messaging/InboxProvider";
import styles from "@components/messaging/Messages.module.css";
import MobileHeader from "@components/MobileHeader";
import Nbsp from "@components/Nbsp";
import Icon from "@components/text/Icon";
import TextWithIcon from "@components/text/TextWithIcon";
import BodyCopy from "@components/typography/BodyCopy";
import Heading2 from "@components/typography/Heading2";
import TextWithHighlight from "@components/typography/TextWithHighlight";
import useContextSafely from "@hooks/useContextSafely";
import useGlobalOpener from "@hooks/useGlobalOpener";
import useMe from "@hooks/useMe";
import useNodesFromConnection from "@hooks/useNodesFromConnection";
import useTimeAgo from "@hooks/useTimeAgo";
import { OpenerTypeEnum } from "@typing/Enums";
import {
  ChatFragment,
  GetJourneyMessagesDocument,
  GetJourneyMessagesQuery,
  GetJourneyMessagesQueryVariables,
  UserTypeEnum
} from "@typing/Generated";
import { getFileImage } from "@utils/fileUtils";
import { imageUrl } from "@utils/imageUtils";
import { getChatUsers, sortByTimestamp } from "@utils/modelUtils";

type JourneyMessage = NonNullable<
  NonNullable<NonNullable<GetJourneyMessagesQuery["journeyMessages"]["edges"]>[number]>["node"]
>;

type FormData = {
  search?: string | null;
};

type ChatProps = {
  chat: ChatFragment;
};

type ChatUserLabelProps = {
  chat: ChatFragment;
};

const ChatUserLabels = ({ chat }: ChatUserLabelProps) => {
  const me = useMe();
  const { journey } = useContextSafely(InboxContext);

  const chatUsers = getChatUsers(me, chat, journey);
  const firstThreeUsers = chatUsers.slice(0, 3);

  return (
    <div className={styles.controlChatUsers}>
      <div className={styles.controlsAvatarsSmall}>
        {firstThreeUsers.map((user, index) => {
          if (index === 2 && chatUsers.length > 3) {
            return (
              <div className={styles.controlsAvatarContainer} key={user.id}>
                <div
                  className={styles.andXOthers}
                  style={{
                    left: (firstThreeUsers.length - index) * 22,
                    top: 30,
                    zIndex: 100000
                  }}
                >
                  <FormattedMessage id="dictionary.punctuation.plus" />
                  {chatUsers.length - 2}
                </div>
              </div>
            );
          }

          return (
            <div className={styles.controlsAvatarContainer} key={user.id}>
              <div
                className={styles.controlsAvatarSmall}
                style={{
                  left: (firstThreeUsers.length - index) * 22,
                  zIndex: firstThreeUsers.length + 1 - index
                }}
              >
                {user.userType === UserTypeEnum.EXPERT && "expert" in user.avatarProps ? (
                  <ExpertAvatar {...user.avatarProps} />
                ) : "firstName" in user.avatarProps ? (
                  <ClientAvatar {...user.avatarProps} clientId={user.id} journeyId={journey.id} />
                ) : null}
              </div>
            </div>
          );
        })}
      </div>
      <BodyCopy bold color="gray-500">
        {chatUsers.length > 3
          ? firstThreeUsers
              .slice(0, 2)
              .map(user => user.name)
              .join(", ")
          : firstThreeUsers.map(user => user.name).join(", ")}
        {chatUsers.length > 3 && (
          <>
            <Nbsp />
            <FormattedMessage id="pages.messages.andXOthers" values={{ count: chatUsers.length - 2 }} />
          </>
        )}
      </BodyCopy>
    </div>
  );
};

const Chat = ({ chat }: ChatProps) => {
  const timeAgo = useTimeAgo(chat.lastMessage?.createdAt);

  const { selectedChat, setEditingChat, setSelectedChat } = useContextSafely(InboxContext);

  const handleChatSelect = (chat: ChatFragment) => {
    setSelectedChat(chat);
    setEditingChat(false);
  };

  return (
    <div className={styles.controlsChatBorder} data-test="chat">
      <div
        className={`${styles.controlsChat} ${selectedChat?.id === chat.id ? styles.controlsChatSelected : ""}`}
        onClick={() => handleChatSelect(chat)}
      >
        <div className={styles.controlsChatHeader}>
          <ChatUserLabels chat={chat} />
          {chat.unreadMessageCount > 0 ? (
            <div className={styles.controlsUnreadMessages}>
              <BodyCopy color="white" size="small">
                {chat.unreadMessageCount}
              </BodyCopy>
            </div>
          ) : chat.lastMessage ? (
            <div className={styles.controlsTimeAgo}>
              <BodyCopy color="gray-500">{timeAgo}</BodyCopy>
            </div>
          ) : null}
        </div>
        <div className={styles.controlsLastMessage}>
          <BodyCopy color="gray-500">{chat.lastMessage?.bodyPlainText}</BodyCopy>
        </div>
      </div>
    </div>
  );
};

const MessagesControls = () => {
  const intl = useIntl();

  const { handleChatCreate, handleMessageSearchFind, journey, messageSearch, setMessageSearch } =
    useContextSafely(InboxContext);

  const [search, setSearch] = useState<string | null | undefined>(null);

  const [query, setQuery] = useQueryParams({
    search: withDefault({ ...StringParam, urlName: "messageSearch" }, search)
  });

  const defaultValues: FormData = query;
  const messagesOpener = useGlobalOpener(OpenerTypeEnum.MESSAGES);

  const form = useForm<FormData>({
    defaultValues,
    mode: "onChange"
  });

  const searchValue = form.watch("search");

  useEffect(() => {
    if (!search) {
      setMessageSearch({ chatId: null, found: null, messageId: null, searchTerm: null });
    }
  }, [search, setMessageSearch]);

  useEffect(() => {
    if (search !== searchValue) {
      setQuery({ search: searchValue });
      setSearch(searchValue);
    }
  }, [setQuery, search, searchValue]);

  const [getJourneyMessages, getJourneyMessagesQuery] = useLazyQuery<
    GetJourneyMessagesQuery,
    GetJourneyMessagesQueryVariables
  >(GetJourneyMessagesDocument, {
    fetchPolicy: "no-cache"
  });

  useEffect(() => {
    if (search) {
      getJourneyMessages({
        variables: {
          id: journey.id,
          search: search ?? ""
        }
      });
    }
  }, [getJourneyMessages, journey.id, search]);

  const journeyMessages = useNodesFromConnection(getJourneyMessagesQuery.data?.journeyMessages);

  const searchResults = useMemo(() => {
    if (!search) return null;

    const fileResources = journeyMessages
      .map(message => message.fileResources.flat())
      .flat()
      .filter(fr => fr.title?.toLowerCase().includes(search.toLowerCase()))
      .map(fr => ({
        fr,
        message: journeyMessages.find(message => message.fileResources.includes(fr)) as JourneyMessage
      }));

    return {
      fileResources,
      messages: journeyMessages
        .filter(message => message.bodyPlainText?.toLowerCase().includes(search.toLowerCase()))
        .slice(0, 3)
    };
  }, [journeyMessages, search]);

  return (
    <div className={styles.controlsFab}>
      <MobileHeader handleBack={messagesOpener.close} title={intl.formatMessage({ id: "pages.messages.title" })} />
      <div className={`${styles.controlsHeader} ion-hide-lg-down`}>
        <Heading2>
          <FormattedMessage id="pages.messages.title" />
        </Heading2>
        <div className={styles.newChat}>
          <div>
            <CircleButton
              ariaLabelKey="accessibility.ariaLabels.new"
              color="gray-500"
              fill="clear"
              icon="penToSquare"
              onClick={handleChatCreate}
              size="medium"
            />
            <CircleButton
              ariaLabelKey="accessibility.ariaLabels.close"
              color="gray-500"
              fill="clear"
              icon="times"
              onClick={messagesOpener.close}
              size="medium"
            />
          </div>
        </div>
      </div>
      <div className={`${styles.controlsSearch} flex gap-md items-center`}>
        <div className="full-width">
          <SearchInput
            error={form.formState.errors.search?.message}
            initialValue={defaultValues.search}
            name="search"
            setValue={v => form.setValue("search", v)}
          />
        </div>

        <div className="ion-hide-lg-up">
          <CircleButton
            ariaLabelKey="accessibility.ariaLabels.new"
            color="gray-500"
            fill="clear"
            icon="penToSquare"
            onClick={handleChatCreate}
            size="large"
          />
        </div>
      </div>
      <div className={styles.controlsAllThread}>
        <TextWithIcon color="primary" icon="messages" text={intl.formatMessage({ id: "pages.messages.allThreads" })} />
      </div>
      <div className={styles.controlsChats}>
        {searchResults ? (
          <div>
            {getJourneyMessagesQuery.loading ? (
              <ChatsLoader />
            ) : (
              searchResults.messages.map(message => (
                <div
                  className={styles.controlsSearchResult}
                  key={message.id}
                  onClick={() => {
                    if (messageSearch.found === null) {
                      handleMessageSearchFind(message.chat.id, message.id, search ?? "");
                    }
                  }}
                >
                  <div className={styles.controlsSearchResultHeader}>
                    <div className="full-width">
                      <ChatUserLabels chat={journey.chats.find(chat => chat.id === message.chat.id) as ChatFragment} />
                    </div>
                    <div className={`${styles.tinySpacing} no-wrap`}>
                      <BodyCopy color="gray-500">
                        <DateStamp mmddyyyy value={message.createdAt} />
                      </BodyCopy>
                    </div>
                    <Icon color="primary" icon="chevronRight" />
                  </div>
                  <div className={styles.controlsLastMessage}>
                    <BodyCopy color="gray-500">
                      <TextWithHighlight extract match={search} text={message.bodyPlainText} />
                    </BodyCopy>
                  </div>
                </div>
              ))
            )}
            {searchResults.fileResources.length > 0 && (
              <div>
                <div className={styles.controlsAllThread}>
                  <TextWithIcon
                    color="primary"
                    icon="paperclip"
                    text={intl.formatMessage({ id: "pages.messages.attachments" })}
                  />
                </div>
                {searchResults.fileResources.map(fileResource => (
                  <div
                    className={styles.controlsSearchResult}
                    key={fileResource.fr.id}
                    onClick={() => {
                      if (messageSearch.found === null) {
                        handleMessageSearchFind(fileResource.message.chat.id, fileResource.message.id, search ?? "");
                      }
                    }}
                  >
                    <div className={styles.controlsSearchResultHeader}>
                      <div className="full-width" />
                      <div className={`${styles.tinySpacing} no-wrap`}>
                        <BodyCopy color="gray-500">
                          <DateStamp mmddyyyy value={fileResource.message.createdAt} />
                        </BodyCopy>
                      </div>
                      {fileResource.message.chat.id === messageSearch.chatId && messageSearch.found === false ? (
                        <Icon color="primary" icon="spinner" />
                      ) : (
                        <Icon color="primary" icon="chevronRight" />
                      )}
                    </div>
                    <div className={styles.controlFile}>
                      <img
                        aria-label={intl.formatMessage({ id: "accessibility.ariaLabels.file" })}
                        className={styles.controlsLargeFileImg}
                        src={imageUrl(getFileImage(fileResource.fr))}
                      />
                      <div className={styles.controlsLastMessage}>
                        <BodyCopy color="gray-500">
                          <TextWithHighlight match={search} text={fileResource.fr.title} />
                        </BodyCopy>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : (
          <>
            {sortByTimestamp(journey.chats, chat => chat.lastMessage?.createdAt).map(chat => (
              <Chat chat={chat} key={chat.id} />
            ))}
          </>
        )}
      </div>
    </div>
  );
};

export default MessagesControls;
